/**
 * Dock glyphs — 20×20 stroke icons in currentColor. Inline SVG keeps the CSP
 * clean (no external assets) and lets the lime accent flow through `color`.
 */

import type { ReactNode } from 'react';

export type IconName =
  | 'prev'
  | 'next'
  | 'play'
  | 'pause'
  | 'replay'
  | 'ask'
  | 'map'
  | 'share'
  | 'deconstruct'
  | 'fullscreen'
  | 'exitFullscreen'
  | 'help'
  | 'lens';

const PATHS: Record<IconName, ReactNode> = {
  prev: <path d="M12 4.5 6.5 10 12 15.5" />,
  next: <path d="m8 4.5 5.5 5.5L8 15.5" />,
  play: <path d="M7 4.6v10.8L16 10z" fill="currentColor" stroke="none" />,
  pause: <path d="M7 4.5v11M13 4.5v11" strokeWidth="2.4" />,
  replay: (
    <>
      <path d="M4.6 10a5.4 5.4 0 1 0 1.7-3.9" />
      <path d="M4.5 3.8v3.2h3.2" />
    </>
  ),
  ask: (
    <>
      <path d="M4 6.2A2.7 2.7 0 0 1 6.7 3.5h6.6A2.7 2.7 0 0 1 16 6.2v4.6a2.7 2.7 0 0 1-2.7 2.7H9.3L5.5 16.4v-2.9A2.7 2.7 0 0 1 4 10.8z" />
      <path d="m10 5.9.8 1.8 1.8.8-1.8.8-.8 1.8-.8-1.8-1.8-.8 1.8-.8z" fill="currentColor" stroke="none" />
    </>
  ),
  map: (
    <>
      <rect x="3.5" y="3.5" width="5.5" height="5.5" rx="1.4" />
      <rect x="11" y="3.5" width="5.5" height="5.5" rx="1.4" />
      <rect x="3.5" y="11" width="5.5" height="5.5" rx="1.4" />
      <rect x="11" y="11" width="5.5" height="5.5" rx="1.4" />
    </>
  ),
  share: (
    <>
      <path d="M10 12.2V3.6M6.8 6.8 10 3.6l3.2 3.2" />
      <path d="M5 10.6v3.9A1.6 1.6 0 0 0 6.6 16h6.8a1.6 1.6 0 0 0 1.6-1.5v-3.9" />
    </>
  ),
  deconstruct: (
    <>
      <path d="M10 3.4 16.4 6.8 10 10.2 3.6 6.8z" />
      <path d="m3.6 10.3 6.4 3.4 6.4-3.4" />
      <path d="m3.6 13.4 6.4 3.4 6.4-3.4" />
    </>
  ),
  fullscreen: <path d="M3.6 7.6V3.6h4M16.4 7.6V3.6h-4M3.6 12.4v4h4M16.4 12.4v4h-4" />,
  exitFullscreen: <path d="M7.6 3.6v4h-4M12.4 3.6v4h4M7.6 16.4v-4h-4M12.4 16.4v-4h4" />,
  help: (
    <>
      <circle cx="10" cy="10" r="6.9" />
      <path d="M7.9 8.1a2.2 2.2 0 1 1 3.2 2c-.7.4-1 .8-1 1.5" />
      <circle cx="10.1" cy="14" r=".7" fill="currentColor" stroke="none" />
    </>
  ),
  lens: (
    <>
      <path d="M2.6 10s3-5.2 7.4-5.2S17.4 10 17.4 10s-3 5.2-7.4 5.2S2.6 10 2.6 10z" />
      <circle cx="10" cy="10" r="2.3" />
    </>
  ),
};

export function Icon({ name }: { name: IconName }) {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {PATHS[name]}
    </svg>
  );
}
